/**
 * Global API Cache Service
 * 
 * Caches API responses by URL to prevent repeated API calls.
 * Fetches happen only once; subsequent requests for the same URL get cached data.
 * 
 * Usage:
 *   const data = await ApiCache.get('https://api.example.com/endpoint');
 *   // subsequent calls with same URL return cached data immediately
 */

class ApiCacheService {
  constructor() {
    this.cache = new Map(); // URL -> Promise<response>
    this.completed = new Map(); // URL -> response data
  }

  /**
   * Fetch from API using axios or fetch and return cached data
   * @param {string} url - API endpoint URL
   * @param {Object} options - { method, headers, data, axiosInstance } 
   * @returns {Promise<Object>} - API response data
   */
  async get(url, options = {}) {
    if (!url) return null;

    // Return cached completed result immediately
    if (this.completed.has(url)) {
      console.log(`🟢 ApiCache HIT: ${url}`);
      return this.completed.get(url);
    }

    // If a fetch is in progress, wait for it
    if (this.cache.has(url)) {
      console.log(`⏳ ApiCache PENDING: ${url}`);
      return this.cache.get(url);
    }

    // Start new fetch and cache the promise
    console.log(`🔄 ApiCache MISS: fetching ${url}`);
    const fetchPromise = this._fetchApi(url, options);
    this.cache.set(url, fetchPromise);

    try {
      const data = await fetchPromise;
      this.completed.set(url, data);
      this.cache.delete(url); // clean up promise cache
      return data;
    } catch (error) {
      this.cache.delete(url);
      console.error(`ApiCache: failed to fetch ${url}`, error);
      throw error;
    }
  }

  async _fetchApi(url, options = {}) {
    const { method = 'GET', headers = {}, data = null, axiosInstance = null } = options;

    if (axiosInstance) {
      // Use provided axios instance (e.g., for auth headers)
      const response = await axiosInstance({
        method,
        url,
        headers,
        data,
      });
      return response.data;
    }

    // Fall back to native fetch
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: data ? JSON.stringify(data) : null,
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache',
    });

    if (!response.ok) {
      throw new Error(`API request failed (${response.status}): ${url}`);
    }

    return response.json();
  }

  /**
   * Batch fetch multiple API endpoints in parallel
   * @param {Array<{url, options}>} requests - Array of { url, options }
   * @returns {Promise<Object>} - { url: data }
   */
  async getBatch(requests) {
    const results = {};
    const promises = requests.map(({ url, options }) =>
      this.get(url, options).then(data => {
        results[url] = data;
      })
    );
    await Promise.all(promises);
    return results;
  }

  /**
   * Invalidate (clear) a cached endpoint
   * @param {string} url - API endpoint URL
   */
  invalidate(url) {
    this.cache.delete(url);
    this.completed.delete(url);
    console.log(`🗑️ ApiCache CLEARED: ${url}`);
  }

  /**
   * Clear all cached data
   */
  clear() {
    this.cache.clear();
    this.completed.clear();
    console.log('🗑️ ApiCache CLEARED: all data');
  }

  /**
   * Get cache size for debugging
   */
  size() {
    return this.completed.size;
  }

  /**
   * Get list of cached URLs for debugging
   */
  getCachedUrls() {
    return Array.from(this.completed.keys());
  }
}

// Export singleton instance
const apiCacheInstance = new ApiCacheService();
export default apiCacheInstance;
